import React from "react";
import { ChangeEvent, MouseEvent } from "react";
import { Link } from "react-router-dom";
import { RouteComponentPropsHistory } from "../../types";

import { apiGet } from "../../Api";
import { formatDateLocal, formatDateTimeLocal, formatPhone } from "../../Utilities";
import withTitle from "../withTitle";
import withProps from "../withProps";

import DelayInput from "../DelayInput";
import HasRight from "../HasRight";
import ViewToolbar from "../ViewToolbar";

import "../../blocks.css";
import "../../icomoon.css";
import "../../indicator.css";
import "../../list.css";
import "../../spinner.css";
import "../../table.css";
import "../../user.css";
import "../viewer.css";

interface IFairLocationsViewState {
    isLoading: boolean;
    error?: string;
    locations: any[];
    showFilter: boolean;

    searchText: string;
    searchOffset: number;
    searchStatus: string;

    sortColumn: string;
    sortDirection: "asc" | "desc";
}

interface IFairLocationsViewProps extends RouteComponentPropsHistory<any, any> {
    mode?: "merge_selection";
}

const PAGE_SIZE = 50;

class FairLocationsView extends React.Component<IFairLocationsViewProps, IFairLocationsViewState> {
    constructor(props: Readonly<IFairLocationsViewProps>) {
        super(props);

        this.state = {
            isLoading: false,
            locations: [],

            searchText: props.location.state && props.location.state.searchText ? props.location.state.searchText : "",
            showFilter: false,

            searchOffset: props.location.state && props.location.state.searchOffset ? props.location.state.searchOffset : 0,
            searchStatus: props.location.state && props.location.state.searchStatus ? props.location.state.searchStatus : "",

            sortColumn: props.location.state && props.location.state.sortColumn ? props.location.state.sortColumn : "lastfair",
            sortDirection: props.location.state && props.location.state.sortDirection ? props.location.state.sortDirection : "asc",
        }

        this.handleInputChanged = this.handleInputChanged.bind(this);
        this.showMapWindow = this.showMapWindow.bind(this);
        this.nextPage = this.nextPage.bind(this);
        this.previousPage = this.previousPage.bind(this);
        this.sortByField = this.sortByField.bind(this);
    }

    public async componentDidMount() {
        this.doSearch();
    }

    public render() {
        const sourceId = (this.props.location.state && this.props.location.state.sourceId) || "";
        return (
            <div className="viewer-root with-actionbar">
                <ViewToolbar>
                    {this.props.mode === "merge_selection"
                        ? null
                        : <HasRight right="fair:create">
                            <Link to="/fairtargets/add" className="icon-plus" />
                        </HasRight>
                    }
                </ViewToolbar>
                {
                    this.props.location.state && this.props.location.state.date ? (
                        <p>Choose an existing target for the health fair below, or add a new one with the button at the top right.</p>
                    ) : null
                }
                {
                    this.props.mode === "merge_selection"
                        ? <p>
                            Select a company to merge with <b>{this.props.location.state ? this.props.location.state.name : null}</b>
                        </p>
                        : null
                }
                <div className="filter-control-box">
                    <DelayInput type="search" className="fill-space" placeholder="Search..." name="searchText" value={this.state.searchText} onChangeDelayed={this.handleInputChanged} timeout={500} />
                    <select name="searchStatus" value={this.state.searchStatus} onChange={this.handleInputChanged}>
                        <option value="">All status</option>
                        <option value="oktocall">OK to call</option>
                        <option value="donotcall">Do not call</option>
                    </select>
                    {/*<button type="button" className="button text-with-icon"><i className="icon-filter" /><span>Filter</span></button>*/}
                </div>
                {
                    this.state.showFilter ?
                        <div className="filter-control-box">
                            <div className="control-group"><label htmlFor="filter-control-min-days">No fair in the last</label><input type="number" id="filter-control-min-days" className="input-tiny" min={0} /> day(s)</div>
                            <div className="control-group"><label htmlFor="filter-control-min-employees">At least</label><input type="number" id="filter-control-min-employees" className="input-tiny" /> employees</div>
                        </div>
                        : ""
                }

                <table className="table-vertical">
                    <thead>
                        <tr>
                            <th className="clickable" onClick={() => this.sortByField("name")}>Name {this.sortIcon("name")}</th>
                            <th>City</th>
                            <th className="col-smallest clickable" onClick={() => this.sortByField("employees")}>Employees {this.sortIcon("employees")}</th>
                            <th className="col-smallest clickable" onClick={() => this.sortByField("faircount")}>Fairs {this.sortIcon("faircount")}</th>
                            <th className="col-smallest clickable" onClick={() => this.sortByField("lastfair")}>Latest health fair {this.sortIcon("lastfair")}</th>
                            <th className="col-smallest"><i className="icon-phone" title="Has phone number?" /></th>
                            <th className="col-smallest"><i className="icon-envelope" title="Has email address?" /></th>
                            <th className="col-smallest" />
                            <th className="col-smallest" />
                        </tr>
                    </thead>
                    <thead className="table-vertical-head-small">
                        <tr>
                            <th>Locations</th>
                        </tr>
                    </thead>
                    <tbody>
                        {
                            this.state.isLoading ?
                                <tr>
                                    <td colSpan={10}>
                                        <div className="spinner-center lds-dual-ring" />
                                    </td>
                                </tr>
                                : this.state.locations.length === 0
                                    ? <tr>
                                        <td colSpan={10}>No results found</td>
                                    </tr>
                                    : this.state.locations.map(a => (
                                        <tr key={a.id} className={this.entryToClass(a)}>
                                            <td className="wrap">{a.name}</td>
                                            <td>
                                                <a href={"https://maps.google.com/maps?daddr=" + encodeURIComponent(`${a.address.street} ${a.address.city}, ${a.address.state}`)} target="_blank" rel="noopener noreferrer" onClick={this.showMapWindow}>{`${a.address.city}, ${a.address.state}`}</a>
                                            </td>
                                            <td data-caption="Employees: ">{a.employeeCount || "N/A"}</td>
                                            <td className="inline" data-caption="Fairs: "><Link to={`/fairtargets/${a.id}/fairs`} title="View fairs for company...">{a.fairCount}</Link></td>
                                            <td className="inline" data-caption="Last fair: " title={formatDateTimeLocal(a.lastFairTimestamp)}>{formatDateLocal(a.lastFairTimestamp)}</td>
                                            <td className="inline" data-caption="Has phone #: ">{a.contact.phone === "0000000000" ? <i className="icon-exclamation-circle red" title="No phone number" /> : <i className="icon-phone" title={`${a.contact.name} \u2014 ${formatPhone(a.contact.phone)}`} />}</td>
                                            <td className="inline" data-caption="Has email: ">{!!a.contact.email ? <i className="icon-envelope" title="Has email address" /> : null}</td>
                                            <td>
                                                {
                                                    this.props.mode === "merge_selection"
                                                        ? null
                                                        : <Link to={{ pathname: "/fairs/add", state: { baseLocation: a, date: (this.props.location.state && this.props.location.state.date) ? this.props.location.state.date : undefined } }} className="button small text-with-icon">
                                                            <i className="icon-plus" />
                                                            <span>Fair</span>
                                                        </Link>
                                                }
                                            </td>
                                            <td>
                                                {
                                                    this.props.mode === "merge_selection"
                                                        ? sourceId === a.id
                                                            ? <button type="button" className="button small text-with-icon" disabled={true}>
                                                                <span>Merge</span>
                                                                <i className="icon-chevron-right" />
                                                            </button>
                                                            : <Link to={{ pathname: `/fairtargets/merge/${sourceId}/${a.id}` }} className="button small text-with-icon">
                                                                <span>Merge</span>
                                                                <i className="icon-chevron-right" />
                                                            </Link>
                                                        : <Link to={{ pathname: `/fairtargets/${a.id}`, state: { date: this.props.location.state && this.props.location.state.date } }} className="button small text-with-icon">
                                                            <span>Details</span>
                                                            <i className="icon-chevron-right" />
                                                        </Link>
                                                }
                                            </td>
                                        </tr>
                                    ))
                        }
                    </tbody>
                </table>
                &nbsp;
                <div className="action-panel action-panel-noflip">
                    <button className="button push-right text-with-icon" onClick={this.previousPage} disabled={this.state.searchOffset === 0}><i className="icon-angle-left" /><span>Previous</span></button>
                    <button className="button text-with-icon" onClick={this.nextPage} disabled={this.state.locations.length < PAGE_SIZE}><span>Next</span><i className="icon-angle-right" /></button>
                </div>
            </div>
        );
    }

    private handleInputChanged(e: ChangeEvent<HTMLInputElement> | ChangeEvent<HTMLSelectElement> | ChangeEvent<HTMLTextAreaElement>) {
        const value = e.currentTarget.value;
        const name = e.currentTarget.name;

        this.setState({
            searchOffset: 0,
            [name]: value
        } as any,
            this.doSearch);
    }

    private async doSearch() {
        this.saveSearchState();

        try {
            this.setState({
                isLoading: true
            });

            const params = [
                `limit=${PAGE_SIZE}`,
                `offset=${this.state.searchOffset}`
            ];

            if (this.state.searchText) {
                params.push(`q=${encodeURIComponent(this.state.searchText)}`);
            }

            if (this.state.searchStatus) {
                params.push(`status=${encodeURIComponent(this.state.searchStatus)}`);
            }

            if (this.state.sortColumn) {
                params.push(`sort_col=${this.state.sortColumn}`);
                params.push(`sort_dir=${this.state.sortDirection || "asc"}`);
            }

            const result = await apiGet<any[]>(`fairlocations/summary?${params.join("&")}`);

            this.setState({
                isLoading: false,
                locations: result,
            });
        }
        catch (e) {
            this.setState({
                error: e.message || e,
                isLoading: false
            });
        }
    }

    private showMapWindow(e: MouseEvent<HTMLAnchorElement>) {
        e.preventDefault();

        window.open(e.currentTarget.href, undefined, "resizable,scrollbars,status");
    }

    private saveSearchState() {
        // Stash the currently displayed month so the user can come back to it
        const currentLocation = this.props.location;
        const newState = {
            searchOffset: this.state.searchOffset,
            searchText: this.state.searchText,
            searchStatus: this.state.searchStatus,
            sortColumn: this.state.sortColumn,
            sortDirection: this.state.sortDirection,
        };
        if (currentLocation.state) {
            Object.assign(currentLocation.state, newState);
        }
        else {
            currentLocation.state = newState;
        }
        this.props.history.replace(currentLocation);
    }

    private nextPage() {
        this.setState(s => {
            return {
                searchOffset: s.searchOffset + PAGE_SIZE
            };
        }, () => this.doSearch());
    }

    private previousPage() {
        this.setState(s => {
            return {
                searchOffset: Math.max(s.searchOffset - PAGE_SIZE, 0)
            };
        }, () => this.doSearch());
    }

    private sortByField(field: string) {
        const direction = this.state.sortColumn === field && this.state.sortDirection === "asc" ? "desc" : "asc";

        this.setState({
            sortColumn: field,
            sortDirection: direction
        }, this.doSearch);
    }

    private sortIcon(column: string) {
        return this.state.sortColumn === column
            ? <i className={`icon-chevron-${this.state.sortDirection === "asc" ? "up" : "down"} icon-style-smaller`} />
            : undefined
    }

    private entryToClass(item: any) {
        switch (item.status) {
            case 2: return "row-gray row-gray-text";
            default: return "";
        }
    }
}

export default withTitle("Health Fair Targets", FairLocationsView);
export const FairLocationsSelectionView = withTitle("Select Target Company", withProps(FairLocationsView, { mode: "merge_selection" }));
